import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

const usePaymentMetrics = ({ startDate, endDate, enabled = true }) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  const formatDateForAPI = (date) => {
    return date ? date.format('YYYY-MM-DD') : null;
  };

  const fetchPaymentMetric = async (endpoint) => {
    const axiosInstance = createAxiosInstance();
    const params = new URLSearchParams();
    
    if (startDate) params.append('startDate', formatDateForAPI(startDate));
    if (endDate) params.append('endDate', formatDateForAPI(endDate));
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `${tenantVal}/payment-insights/${endpoint}${queryString}`;
    
    const response = await axiosInstance.get(url);
    return response.data;
  };

  const dailyAveragePaymentsDue = useQuery({
    queryKey: ["daily-average-payments-due", tenantVal, startDate, endDate],
    queryFn: () => fetchPaymentMetric("daily-average-payments-due"),
    enabled: enabled && !!tenantVal,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const dailyAveragePaymentsMade = useQuery({
    queryKey: ["daily-average-payments-made", tenantVal, startDate, endDate],
    queryFn: () => fetchPaymentMetric("daily-average-payments-made"),
    enabled: enabled && !!tenantVal,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const averageDaysToPay = useQuery({
    queryKey: ["average-days-to-pay", tenantVal, startDate, endDate],
    queryFn: () => fetchPaymentMetric("average-days-to-pay"),
    enabled: enabled && !!tenantVal,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const averageDaysOpen = useQuery({
    queryKey: ["average-days-open", tenantVal, startDate, endDate],
    queryFn: () => fetchPaymentMetric("average-days-open"),
    enabled: enabled && !!tenantVal,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const combinedDailyPaymentSummary = useQuery({
    queryKey: ["combined-daily-payment-summary", tenantVal, startDate, endDate],
    queryFn: () => fetchPaymentMetric("combined-daily-payment-summary"),
    enabled: enabled && !!tenantVal,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const totalPaymentsDue = useQuery({
    queryKey: ["total-payments-due-for-date-range", tenantVal, startDate, endDate],
    queryFn: () => fetchPaymentMetric("total-payments-due-for-date-range"),
    enabled: enabled && !!tenantVal,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const totalPaymentsMade = useQuery({
    queryKey: ["total-payments-made-for-date-range", tenantVal, startDate, endDate],
    queryFn: () => fetchPaymentMetric("total-payments-made-for-date-range"),
    enabled: enabled && !!tenantVal,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    dailyAveragePaymentsDue,
    dailyAveragePaymentsMade,
    averageDaysToPay,
    averageDaysOpen,
    combinedDailyPaymentSummary,
    totalPaymentsDue,
    totalPaymentsMade,
    isLoading: dailyAveragePaymentsDue.isLoading || dailyAveragePaymentsMade.isLoading || 
               averageDaysToPay.isLoading || averageDaysOpen.isLoading || combinedDailyPaymentSummary.isLoading ||
               totalPaymentsDue.isLoading || totalPaymentsMade.isLoading,
    isError: dailyAveragePaymentsDue.isError || dailyAveragePaymentsMade.isError || 
             averageDaysToPay.isError || averageDaysOpen.isError || combinedDailyPaymentSummary.isError ||
             totalPaymentsDue.isError || totalPaymentsMade.isError
  };
};

export { usePaymentMetrics };